import { useCallback, useState, useEffect, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

type RequestWithShift = {
  id: string;
  shiftId: string | null;
};

type UseShiftChangeRequestSidebarProps<T extends RequestWithShift> = {
  requests: T[];
  loading: boolean;
};

export const useShiftChangeRequestSidebar = <T extends RequestWithShift>({
  requests,
  loading,
}: UseShiftChangeRequestSidebarProps<T>) => {
  const location = useLocation();
  const history = useHistory();
  const [selectedShift, setSelectedShift] = useState<T | null>(null);

  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const shiftChangeRequestId = queryParams.get('shiftChangeRequestId');

  useEffect(() => {
    if (!shiftChangeRequestId) {
      setSelectedShift(null);
      return;
    }

    if (!loading && requests.length > 0) {
      const matchingRequest = requests.find(
        (req) => req.id === shiftChangeRequestId && req.shiftId,
      );
      if (matchingRequest) {
        setSelectedShift(matchingRequest);
      } else {
        setSelectedShift(null);
      }
    }
  }, [shiftChangeRequestId, loading, requests]);

  const handleShiftClick = useCallback(
    (request: T) => {
      if (request.shiftId) {
        const newQuery = new URLSearchParams(location.search);
        newQuery.set('shiftChangeRequestId', request.id);
        history.push({ search: newQuery.toString() });
        setSelectedShift(request);
      }
    },
    [location.search, history],
  );

  const handleCloseSidebar = useCallback(() => {
    const newQuery = new URLSearchParams(location.search);
    newQuery.delete('shiftChangeRequestId');
    history.push({ search: newQuery.toString() });
    setSelectedShift(null);
  }, [location.search, history]);

  return {
    selectedShift,
    handleShiftClick,
    handleCloseSidebar,
  };
};
